import { type Config } from '@common/clients/api/client';
import { type PlatformID } from '@common/clients/api/types.gen';
import { type ContextData } from '@common/defaults/ContextData';
import { type UserContext } from '@common/defaults/UserContext';

import { type BaseClientOptions, createBaseClient } from './BaseClient';
import type { ApiFetchOptions } from './types';

export const createCrossPlatformApiClient = ({
    contextData,
    userContext,
    platform,
    isClientSide,
    fetchOptions,
}: {
    contextData: Pick<ContextData, 'config'> & Partial<ContextData>;
    userContext: UserContext | undefined;
    platform: PlatformID;
    isClientSide: boolean;
    fetchOptions?: Config & ApiFetchOptions;
}) => {
    const config = contextData.config?.crossPlatform?.[platform];

    if (!config) {
        throw new Error(`No configuration found for platform: ${platform}`);
    }

    const clientConfig = {
        useNextClient: true,
        useNewRelic: true,
        useStatusCheck: true,
    } satisfies BaseClientOptions;

    const baseUrl = isClientSide || !config.internalHost ? config.host : config.internalHost;

    const defaultFetchOptions = {
        credentials: isClientSide ? 'same-origin' : 'include',
        cache: 'force-cache',
    } satisfies RequestInit;

    const client = createBaseClient<true>(clientConfig, {
        isClientSide,
        userContext,
        fetchOptions: {
            ...defaultFetchOptions,
            ...fetchOptions,
            baseUrl,
            timeout: fetchOptions?.timeout || config.timeout || 5000,
        },
    });

    return client;
};
